
import { PageSection } from "@/lib/singularity/page-structure/page-structure";
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import Icon from "@/components/UI/Icon.vue";
import { updateSectionFriendlyName } from "@/lib/singularity/page-structure/page-structure";
import { documentStore, singularityStore, uiStore } from "@/store/store";
import NiceButton from "@/components/UI/NiceButton.vue";
import { findNode } from "@/lib/tree/tree";
import SingularityImageMediaPreview from "@/components/Singularity/ImageEditor/SingularityImageMediaPreview.vue";
import { getPromptInfoFromNode } from "@/lib/singularity/util";
import { stopInlineEditing } from "@/lib/inline-editor/inline-editor";
import lodash from "lodash";
import SectionCard from "@/components/Singularity/SingularitySectionLibraryModal/SectionCard.vue";
import { getSectionCardDataFromNode } from "@/lib/singularity/section-add-or-swap";
import InlineTextEdit from "@/components/Singularity/components/InlineTextEdit.vue";
// import PageStructureSectionColorsNew from "./PageStructureSectionColorsNew.vue";
import PageStructureSectionColorsNewV2 from "./PageStructureSectionColorsNewV2.vue";
import { logEvent } from "@/util/tracking";
import EditWithAiButton from "@/components/Singularity/components/EditWithAiButton.vue";

@Component({
  components: {
    Icon,
    NiceButton,
    SingularityImageMediaPreview,
    SectionCard,
    InlineTextEdit,
    // PageStructureSectionColorsNew,
    PageStructureSectionColorsNewV2,
    EditWithAiButton
  }
})
export default class PageStructureSection extends Vue {
  @Prop()
  value!: PageSection;

  @Prop()
  index!: number;

  @Prop({ default: false })
  active!: boolean;

  @Prop({ default: false })
  dragOperationInProgress!: boolean;

  @Prop({ default: false })
  currentlyBeingDragged!: boolean;

  isRewriteModalOpen = false;

  get sectionIdToOperateOn() {
    return this.value.nodeId;
  }

  get nodeToOperateOn() {
    return findNode(documentStore.document.tree, this.value.nodeId);
  }

  get sectionCardData() {
    return getSectionCardDataFromNode(this.value.nodeId);
  }

  moveSection(direction: "up" | "down") {
    const newIndex = direction === "up" ? this.index - 1 : this.index + 1;
    documentStore.moveElement({
      draggedElementId: this.value.nodeId,
      droppedOnElementId: documentStore.document.tree.root.id,
      index: newIndex
    });

    uiStore.activateElement(this.value.nodeId);
  }

  duplicateSection() {
    documentStore.duplicateElementAndActivateIt(this.value.nodeId);
  }

  deleteSection() {
    documentStore.deleteElement({ nodeId: this.value.nodeId });
  }

  updateSectionFriendlyName(newTitle: string) {
    updateSectionFriendlyName(this.value.nodeId, newTitle);
  }

  get promptInfo() {
    if (!this.nodeToOperateOn) return undefined;

    return getPromptInfoFromNode(this.nodeToOperateOn);
  }

  get title() {
    return (
      (this.nodeToOperateOn?.data.properties?.meta?.friendlyName?.trim() ||
        this.promptInfo?.singularity.sectionData.type ||
        "unknown title") ??
      undefined
    );
  }

  get hasBackgroundImage() {
    const section = findNode(documentStore.document.tree, this.value.nodeId);

    return (
      section &&
      lodash.get(section.data.properties, "design.background.type") == "image"
    );
  }

  editBackgroundImage() {
    if (!this.sectionIdToOperateOn) return;
    singularityStore.setImageEditorPanelState({
      elementId: this.sectionIdToOperateOn,
      controlPath: "design.background.image",
      initialStep: "options"
    });
    singularityStore.setSingularityLeftPanel("image_editor");
    logEvent("FutureLayer Edit Section Background Click");
  }

  openSectionLibrary(mode: "add_after" | "swap") {
    if (!this.sectionIdToOperateOn) return;

    logEvent("FutureLayer Section Library Opened", {
      mode
    });

    singularityStore.setSectionAddOrSwapPanelState({
      mode,
      sectionId: this.sectionIdToOperateOn
    });
    singularityStore.setSectionLibraryModalState(true);
  }

  get classList() {
    return {
      "currently-being-dragged": this.currentlyBeingDragged,
      "drag-operation-in-progress": this.dragOperationInProgress,
      "page-structure-section": true,
      "page-structure-section--active": this.active,
      "page-structure-section--open": this.active,
      "page-structure-section--currently-rewriting":
        this.isSectionCurrentlyRewritingWithAi
    };
  }

  get isSectionCurrentlyRewritingWithAi() {
    return (
      singularityStore.aiJobs.filter(
        job =>
          "elementId" in job.context &&
          job.context.elementId === this.sectionIdToOperateOn &&
          (job.status === "running" || job.status === "pending") &&
          job.context.jobType === "section_rewrite"
      ).length > 0
    );
  }

  get isSectionCurrentlyReplacingImagesWithAi() {
    return (
      singularityStore.aiJobs.filter(
        job =>
          "elementId" in job.context &&
          job.context.elementId === this.sectionIdToOperateOn &&
          (job.status === "running" || job.status === "pending") &&
          job.context.jobType === "section_image_replacement"
      ).length > 0
    );
  }

  async activateSection() {
    // Don't await this, let it run in the background to avoid blocking the UI.
    stopInlineEditing();
    uiStore.activateElement(this.value.nodeId);
  }

  get activeElementId() {
    return uiStore.activeElementId;
  }
}
